import { STORAGE_KEYS } from "./StorageKeys";
import { getCookie, getLocalStorage } from "./utils/environment";

/**
 * Manages storage operations
 */
export class StorageManager {
  private storage = getLocalStorage();

  /**
   * Set an item in storage
   * @param key Storage key
   * @param value Storage value
   */
  public setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  /**
   * Get an item from storage
   * @param key Storage key
   * @returns The stored value or null if not found
   */
  public getItem(key: string): string | null {
    return this.storage.get(key);
  }

  /**
   * Remove an item from storage
   * @param key Storage key
   */
  public removeItem(key: string): void {
    this.storage.remove(key);
  }

  /**
   * Set an object in storage (serialized as JSON)
   * @param key Storage key
   * @param value Object to store
   */
  public setObject<T>(key: string, value: T): void {
    this.storage.set(key, JSON.stringify(value));
  }

  /**
   * Get an object from storage (deserialized from JSON)
   * @param key Storage key
   * @returns The stored object or null if not found or invalid
   */
  public getObject<T>(key: string): T | null {
    const value = this.storage.get(key);
    if (!value) return null;

    try {
      return JSON.parse(value) as T;
    } catch (error) {
      console.error("Error parsing stored object:", error);
      return null;
    }
  }

  /**
   * Gets country code from storage or cookies
   * @returns The country code or null if not found
   */
  public getCountryCode(): string | null {
    // First try to get from localStorage
    const storedCountry = this.getItem(STORAGE_KEYS.COUNTRY);
    if (storedCountry) return storedCountry;

    // If not in localStorage, try to get from cookies
    const cookieCountry = getCookie("country");

    // If found in cookies, store in localStorage for future use
    if (cookieCountry) {
      this.setItem(STORAGE_KEYS.COUNTRY, cookieCountry);
      return cookieCountry;
    }

    return null;
  }

  /**
   * Sets the country code in storage
   * @param countryCode The country code to store
   */
  public setCountryCode(countryCode: string): void {
    this.setItem(STORAGE_KEYS.COUNTRY, countryCode);
  }

  /**
   * Gets locale from storage or cookies
   * @returns The locale or null if not found
   */
  public getLocale(): string | null {
    // First try to get from localStorage
    const storedLocale = this.getItem(STORAGE_KEYS.LOCALE);
    if (storedLocale) return storedLocale;

    // If not in localStorage, try to get from cookies
    const cookieLocale = getCookie("locale");

    // If found in cookies, store in localStorage for future use
    if (cookieLocale) {
      this.setItem(STORAGE_KEYS.LOCALE, cookieLocale);
      return cookieLocale;
    }

    return "EN";
  }

  /**
   * Sets the locale in storage
   * @param locale The locale to store
   */
  public setLocale(locale: string): void {
    this.setItem(STORAGE_KEYS.LOCALE, locale);
  }
}
